import { applyUiChunk, createUiChunkSessionState } from "./processUiChunkStream";

/**
 * Pure reducer for the `team_child_activity` relay. Folds one relay event into the
 * `Map<runId, entry>` of per-node team member activity and returns the next map — or the SAME
 * reference when the event is a no-op (missing key, finish for an unknown run, or a replay after
 * the run already settled) so the caller can skip a state update.
 *
 * Keyed by runId (one node activation in one user turn) so the same node running again in a later
 * turn gets a fresh card instead of reusing the previous one. `start` opens a card, `chunk` feeds
 * the shared UI-chunk reducer into the run's own message array (so the card streams identically to
 * the agent chat), and `finish` settles the card.
 */
export function reduceTeamActivity(byRun, payload) {
  const { runId, nodeId, agentId, agentName, childSessionId, phase, chunk, label } =
    payload;
  // runId falls back to nodeId for resilience if an older backend omits it.
  const key = runId || nodeId;
  if (!key) return byRun;

  const prev = byRun[key];

  if (phase === "finish") {
    if (!prev) return byRun;
    return {
      ...byRun,
      [key]: { ...prev, status: "done", finishedAt: Date.now() },
    };
  }

  let entry = prev;
  if (!entry) {
    entry = {
      runId: key,
      nodeId: nodeId ?? null,
      agentId: agentId ?? null,
      agentName: agentName ?? null,
      childSessionId: childSessionId ?? null,
      label: label ?? null,
      status: "running",
      cursor: createUiChunkSessionState(childSessionId ?? key),
      messages: [],
      persistedMessages: null,
      startedAt: Date.now(),
      finishedAt: null,
    };
  } else if (entry.status === "done") {
    return byRun; // replay after completion
  }

  if (phase === "start") {
    entry = {
      ...entry,
      nodeId: entry.nodeId ?? nodeId ?? null,
      agentId: entry.agentId ?? agentId ?? null,
      agentName: entry.agentName ?? agentName ?? null,
      childSessionId: entry.childSessionId ?? childSessionId ?? null,
      label: entry.label ?? label ?? null,
    };
  } else if (phase === "chunk" && chunk) {
    const result = applyUiChunk(entry.cursor, chunk, entry.messages);
    entry = { ...entry, cursor: result.cursor, messages: result.messages };
  }

  return { ...byRun, [key]: entry };
}

/**
 * Optimistically flip a worker's inline `ask_human` card to its ANSWERED state inside the team
 * activity map. The worker's resumed activity relays new chunks over the parent stream but never
 * re-emits the (now answered) ask_human part, so without this the embedded card would spin until
 * the run settles / the card is re-expanded. Only the one matching part's output is mutated (in
 * both the live `messages` and any fetched `persistedMessages`), leaving the rest of the stream
 * untouched so subsequent live worker activity keeps flowing. The backend has already persisted
 * the same flip on the child session, so a later transcript refetch stays consistent.
 *
 * Returns the SAME map reference when nothing matched (so the caller can skip the state update).
 */
export function markAskHumanAnswered(byRun, questionId, answerPatch) {
  if (!byRun || !questionId) return byRun;
  const target = String(questionId);

  const flipParts = (msgs) => {
    if (!Array.isArray(msgs) || msgs.length === 0) return msgs;
    let changed = false;
    const next = msgs.map((msg) => {
      if (!msg || !Array.isArray(msg.parts)) return msg;
      let msgChanged = false;
      const parts = msg.parts.map((part) => {
        if (
          part?.type === "tool" &&
          part.toolName === "ask_human" &&
          part.output &&
          typeof part.output === "object" &&
          String(part.output.questionId) === target &&
          part.output.status !== "ANSWERED"
        ) {
          msgChanged = true;
          return { ...part, output: { ...part.output, ...answerPatch } };
        }
        return part;
      });
      if (!msgChanged) return msg;
      changed = true;
      return { ...msg, parts };
    });
    return changed ? next : msgs;
  };

  let changed = false;
  const nextByRun = {};
  for (const [runId, entry] of Object.entries(byRun)) {
    const nextMessages = flipParts(entry?.messages);
    const nextPersisted = flipParts(entry?.persistedMessages);
    if (nextMessages !== entry?.messages || nextPersisted !== entry?.persistedMessages) {
      changed = true;
      nextByRun[runId] = { ...entry, messages: nextMessages, persistedMessages: nextPersisted };
    } else {
      nextByRun[runId] = entry;
    }
  }
  return changed ? nextByRun : byRun;
}
